type Environment = 'development' | 'stage' | 'prod';

/**
 * Labels sent on Amplitude events as `[Amplitude] Environment` for dashboard filters.
 * Align these with Amplitude project environment settings and saved charts.
 */
export const AMPLITUDE_METRICS_ENVIRONMENT: Record<Environment, string> = {
  development: 'development',
  stage: 'staging',
  prod: 'production',
};

/**
 * Amplitude Analytics browser API key and Experiment deployment keys. These are
 * public client identifiers (same visibility as prior `VITE_*` env wiring); they ship in the bundle.
 */
const AMPLITUDE_API_KEY = '3c802765afa7afafe1dc59292f03d7de';

const AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_STAGING =
  'client-3n7OPTADAN7ABV1rjxuMo9tFUWIIHbwT';
const AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_PRODUCTION =
  'client-bfvkLQ18b077LoS5BQiV7bpnywCEydv3';

export const getAmplitudeMetricsEnvironmentLabel = (
  environment: Environment
): string => AMPLITUDE_METRICS_ENVIRONMENT[environment];

interface Config {
  mode: string;
  sentryDsn: string;
  environment: Environment;
  amplitudeId: string;
  /** Amplitude Experiment deployment key (feature flags); from {@link getAmplitudeExperimentDeploymentKey} */
  amplitudeExperimentDeploymentKey: string;
  knockPublicApiKey: string;
  knockFeedChannelId: string;
}

export const getEnvironment = (mode: string): Environment => {
  if (mode !== 'production') {
    return 'development';
  }

  if (window.location.hostname.includes('stage.getsurface.ai')) {
    return 'stage';
  }

  return 'prod';
};

/**
 * Single Amplitude Analytics project API key for all hosted environments (stage + prod).
 */
export const getAmplitudeApiKey = (): string => {
  return AMPLITUDE_API_KEY;
};

/**
 * Deployment keys for Amplitude Experiment (feature flags). Staging vs production is chosen
 * at runtime by hostname / dev server; local Vite dev always uses the staging deployment.
 */
export const getAmplitudeExperimentDeploymentKey = (): string => {
  const staging = AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_STAGING;
  const production = AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_PRODUCTION;

  // Vite dev server: always Experiment staging deployment (same as hosted staging flags).
  if (import.meta.env.DEV) {
    return staging;
  }

  if (typeof window !== 'undefined') {
    if (window.location.hostname.includes('stage.getsurface.ai')) {
      return staging;
    }
    if (import.meta.env.MODE === 'production') {
      return production;
    }
  }

  return staging || production;
};

/**
 * Which Amplitude Experiment deployment (staging vs production) the active
 * {@link getAmplitudeExperimentDeploymentKey} corresponds to.
 */
export const getAmplitudeExperimentDeploymentLabel = ():
  'staging' | 'production' => {
  const key = getAmplitudeExperimentDeploymentKey();
  if (key === AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_PRODUCTION) {
    return 'production';
  }
  if (key === AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_STAGING) {
    return 'staging';
  }
  return 'staging';
};

/** Matches {@link ExperimentConfig.instanceName} default in @amplitude/experiment-js-client */
export const AMPLITUDE_EXPERIMENT_INSTANCE_NAME = '$default_instance';

/** Last 6 characters of the active deployment key (Amplitude SDK localStorage namespace suffix). */
export const getAmplitudeExperimentDeploymentSuffix = (): string =>
  getAmplitudeExperimentDeploymentKey().slice(-6);

/** Amplitude SDK localStorage key for cached variant assignments (`amp-exp-*`). */
export const getAmplitudeExperimentVariantStorageKey = (): string =>
  `amp-exp-${AMPLITUDE_EXPERIMENT_INSTANCE_NAME}-${getAmplitudeExperimentDeploymentSuffix()}`;

/** Amplitude SDK localStorage key for cached flag configs (`amp-exp-*-flags`). */
export const getAmplitudeExperimentFlagsStorageKey = (): string =>
  `${getAmplitudeExperimentVariantStorageKey()}-flags`;

export const getKnockPublicApiKey = (environment: Environment): string => {
  switch (environment) {
    case 'prod':
      return 'pk_NEPNXmXybuQg3ee485QPnJqJSMJQj_PX10fxIJCYZX8';
    case 'stage':
      return 'pk_aT2iW9cHf4vH3gj-8-A-c6TjA4CrUBXO0jVWpthxO5E';
    case 'development':
      // This is the staging key
      return 'pk_aT2iW9cHf4vH3gj-8-A-c6TjA4CrUBXO0jVWpthxO5E';

    // Knock does have a development environment, but for most use cases in dev for the react app
    // you'll want to use the stage environment due to the vite proxy. Keep in mind that if you'd like to use
    // full local development and development knock for notifications, you'll want to make sure that
    // your user id in development knock matches the user id in your local database or you won't see notifications.
    // This is the development key
    // return 'pk_test_dWLWqZQvdREqDcY35tCwjhpVAFfUqrNAP-1ABqQgHMk';
    default:
      return '';
  }
};

// In-App channel ID, configured for all three environments.
// https://dashboard.knock.app/surfaceai/settings/integrations/channels/Q2hhbm5lbDplYWQzOTQzYy1kNzRmLTRlODgtYTdhZC1kN2M1MWJjNmY1YjE=
export const getKnockFeedChannelId = (): string => {
  return 'ead3943c-d74f-4e88-a7ad-d7c51bc6f5b1';
};

export const getConfig = (): Config => {
  const { VITE_SENTRY_DSN, MODE } = import.meta.env;

  const mode = MODE;
  const environment = getEnvironment(mode);
  const amplitudeId = getAmplitudeApiKey();
  const amplitudeExperimentDeploymentKey =
    getAmplitudeExperimentDeploymentKey();
  const knockPublicApiKey = getKnockPublicApiKey(environment);
  const knockFeedChannelId = getKnockFeedChannelId();

  return {
    mode,
    sentryDsn: VITE_SENTRY_DSN || '',
    environment,
    amplitudeId,
    amplitudeExperimentDeploymentKey,
    knockPublicApiKey,
    knockFeedChannelId,
  };
};
